import { createBrowserClient } from "@supabase/ssr";
import { createClient as createNativeClient } from "@supabase/supabase-js";
import { Capacitor } from "@capacitor/core";

const supabaseUrl = process.env.VITE_SUPABASE_URL;
const supabaseKey = process.env.VITE_SUPABASE_ANON_KEY;

let cachedClient: ReturnType<typeof buildClient> | null = null;

// The Capacitor WebView must NOT keep the Supabase session in cookies (the
// @supabase/ssr default): document.cookie on iOS's capacitor://localhost
// custom scheme doesn't persist, so the session evaporates on the post-login
// hard reload and every login bounces straight back to /login. Cookies only
// exist for the web app anyway — the Hono server reads them via cookie-bridge
// on the same origin, while native /api calls authenticate with a bearer
// token (apiFetch). localStorage is the Supabase-recommended session storage
// for Capacitor.
//
// The protocol check is a bridge-independent fallback: if the Capacitor JS
// bridge fails to inject (e.g. WKAppBoundDomains misconfig),
// isNativePlatform() lies and reports "web" — but the capacitor:// scheme
// can't.
const isNativeContext = () =>
  Capacitor.isNativePlatform() ||
  (typeof window !== "undefined" && window.location.protocol === "capacitor:");

const buildWebClient = () => createBrowserClient(supabaseUrl!, supabaseKey!);

const buildClient = (): ReturnType<typeof buildWebClient> =>
  isNativeContext()
    ? (createNativeClient(supabaseUrl!, supabaseKey!, {
        auth: {
          storage: window.localStorage,
          persistSession: true,
          autoRefreshToken: true,
          detectSessionInUrl: true,
          // Match @supabase/ssr's default so auth links behave the same on
          // both client variants.
          flowType: "pkce",
        },
      }) as ReturnType<typeof buildWebClient>)
    : buildWebClient();

export const createClient = () => {
  if (!cachedClient) cachedClient = buildClient();
  return cachedClient;
};
